<template>
    <div>
        <Teleport to="[data-header-action]" v-if="isMounted">
            <div class="[ flex gap-3 ]">
                <ButtonComponent v-if="selected.length" severity="danger" label="Delete" outlined
                    @click="deleteDocuments">
                    <template #icon>
                        <JBIcon name="unavailable" />
                    </template>
                </ButtonComponent>
                <hr>
                <ButtonComponent label="Add documents" outlined @click="openDocumentUploadModal">
                    <template #icon>
                        <JBIcon name="file-upload" />
                    </template>
                </ButtonComponent>
                <ButtonComponent label="Help center" outlined @click="showHelpCenter = true">
                    <template #icon>
                        <JBIcon name="help" />
                    </template>
                </ButtonComponent>
            </div>
        </Teleport>
        <DataTable v-model:selection="selected" :value="documents" scrollable scroll-height="flex" :loading="loading"
            sort-field="date" :sort-order="-1">
            <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
            <template #empty>
                <div class="[ flex flex-col gap-4 -mx-4 ]">
                    <Empty icon="unavailable" title="No documents" message="Upload documents to get started." />
                </div>
            </template>

            <Column field="Title" header="Name" :sortable="true">
                <template #body="{ data }: { data: CorpusDocument }">
                    <div class="[ flex items-center gap-3 ]">
                        <a :href="getDocumentUrl(data.Id)" target="_blank" class="[ hover:underline ]">{{ data.Title
                            }}</a>
                    </div>
                </template>
            </Column>
            <Column field="CatalogName" header="Catalog" :sortable="true">
                <template #body="{ data }: { data: CorpusDocument }">
                    <div class="[ flex items-center gap-3 ]">
                        <Tag v-if="data.CatalogName !== 'global' && data.Id != selectedDocumentId"
                            class="[ cursor-pointer ]" :value="data.CatalogName"
                            @click="selectedDocumentId = data.Id" />
                        <Select v-else placeholder="Choose catalog" :options="catalogs" option-label="Name"
                            @change="handleCatalogSelectChange(data.Id, $event)">
                            <template #footer>
                                <ButtonComponent outlined label="Add new" class="[ w-full ]"
                                    @click="openCreateCatalogDialog(data.Id)" />
                            </template>
                        </Select>
                    </div>
                </template>
            </Column>
        </DataTable>
        <JBDrawer v-model:model-value="showUploadDocumentModal" title="Upload documents">
            <div class="[ flex flex-col gap-4 ]">
                <div ref="dropContainer"
                    class="[ flex flex-1 flex-col justify-between bg-gray/10 border border-dashed rounded-lg aspect-video p-4 ]"
                    :class="{
                        'bg-primary/10 border-primary': isDragging,
                    }">
                    <div class="[ flex flex-1 flex-col gap-4 mx-auto max-w-[300px] items-center justify-center ]">
                        <div class="[ bg-highlight-light p-2 rounded-lg ]">
                            <JBIcon name="file-upload" class="[ text-primary w-6 h-6 ]" />
                        </div>
                        <div class="[ text-center ]">
                            <h2 class="[ font-bold ]">Upload documents</h2>
                            <p class="[ text-gray-dark ]">
                                Drop PDFs here to add it to your corpus
                            </p>
                        </div>

                        <FileUpload name="file" mode="basic" auto custom-upload choose-label="Click to upload"
                            @select="onFileSelected" :choose-button-props="{
                                outlined: true,
                            }">
                            <template #chooseicon>
                                <JBIcon name="cloud-upload" />
                            </template>
                        </FileUpload>
                    </div>
                </div>
                <div class="[ flex gap-8 py-4 px-2 ]" v-for="upload in uploads">
                    <div class="[ flex-1 truncate ]">{{ upload.name }}</div>
                    <div class="[ whitespace-nowrap ]">{{ upload.size }}</div>
                    <div>{{ upload.status }}</div>
                </div>
            </div>
        </JBDrawer>

        <JBDialog v-model:visible="isCatalogInputVisible" header="Create catalog">
            <form @submit.prevent="updateDocumentCatalog(selectedDocumentId!, newCatalogName)">
                <InputText v-model="newCatalogName" placeholder="Catalog name" />
            </form>
            <template #footer>
                <ButtonComponent label="Create" @click="updateDocumentCatalog(selectedDocumentId!, newCatalogName)" />
            </template>
        </JBDialog>

        <JBDrawer v-model:model-value="showHelpCenter" title="Help Center">
            <QueryCenter class="[ min-h-96 ]" no-queue />
        </JBDrawer>
    </div>
</template>

<script setup lang="ts">
import { Button as ButtonComponent, DataTable, Column, FileUpload, FileUploadSelectEvent, Tag, Select, SelectChangeEvent, InputText } from "primevue";
import { onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import JBIcon from "@/components/JBIcon.vue";
import { useDragTarget } from "@/utils/drag-target";
import { Catalog, CorpusDocument, useApi2 } from "@/services/api2";
import { useMounted } from "@vueuse/core";
import Empty from "@/components/ui/Empty.vue";
import { useApiLoader } from "@/services/api";
import { humanFileSize } from "@/utils/file";
import JBDrawer from "@/components/ui/modals/JBDrawer.vue";
import { useDocumentStore } from "@/store/documents";
import JBDialog from "@/components/ui/modals/JBDialog.vue";
import QueryCenter from "@/components/contact/realtime/ui/QueryCenter.vue";

definePage({
    meta: {
        title: "Documents",
        icon: "file-upload",
    },
});

const isMounted = useMounted();

const showUploadDocumentModal = ref(false);
const isCatalogInputVisible = ref(false);
const showHelpCenter = ref(false);
const selectedDocumentId = ref<number | null>(null);
const newCatalogName = ref('');
const selected = ref<CorpusDocument[]>([]);

const catalogs = ref<Catalog[]>([]);

const dropContainer = ref<HTMLElement>();
const { isDragging } = useDragTarget(dropContainer, handleFiles);

const { uploads } = storeToRefs(useDocumentStore());

const { uploadDocument, getDocumentUploadStatus, getDocuments, getDocumentUrl, getCatalogs, updateDocument, deleteDocument } = useApi2();

const { load, data: documents, loading } = useApiLoader(async () => {
    return await getDocuments();
}, []);

function openDocumentUploadModal() {
    showUploadDocumentModal.value = true;
}
function onFileSelected() { }

function useUploadWatcher(instanceId: string, onUpdate: (stats: Awaited<ReturnType<typeof getDocumentUploadStatus>>) => void) {
    let timer: number | null = null;
    async function update() {
        const stats = await getDocumentUploadStatus(instanceId);
        onUpdate(stats);
        if (stats.status === 'errored' || stats.status === 'complete') {
            load();
            return;
        }
        timer = window.setTimeout(() => {
            update();
        }, 3000);
    }

    function start() {
        update();
    }
    function stop() {
        timer && clearInterval(timer);
    }

    return { start, stop };
}

async function handleFiles(files: File[]) {
    const tasks = files.map(async file => {
        const { instanceId } = await uploadDocument(file);
        uploads.value[instanceId] = { instanceId, status: "queued", name: file.name, size: humanFileSize(file.size) };
        const { start } = useUploadWatcher(instanceId, (stats) => { uploads.value[instanceId].status = stats.status; });
        start();
    });

    await Promise.all(tasks);
}

function openCreateCatalogDialog(docId: number) {
    isCatalogInputVisible.value = true;
    selectedDocumentId.value = docId;
}

async function handleCatalogSelectChange(docId: number, event: SelectChangeEvent) {
    await updateDocumentCatalog(docId, event.value.Name);
}

async function updateCatalogs() {
    catalogs.value = await getCatalogs();
}

async function updateDocumentCatalog(docId: number, catalog: string) {
    isCatalogInputVisible.value = false;
    newCatalogName.value = '';
    selectedDocumentId.value = null;

    await updateDocument(docId, { catalog });
    load();
    updateCatalogs();
}

async function deleteDocuments() {
    await Promise.all(selected.value.map(async doc => { await deleteDocument(doc.Id); }));
    selected.value = [];
    load();
}

onMounted(() => {
    load();
    updateCatalogs();
});

</script>